// BLV cinematic homepage — Phase 1 visual redesign.
// Content is data-driven (DEPARTMENTS / BENEFITS / panels from characters.js) so assets can be swapped later.
import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import { CinematicIntro } from "@/components/cinema/CinematicIntro";
import { windowState } from "@/lib/windows";
import { MissionCountdown } from "@/components/recruitment/MissionCountdown";
import { CountdownShareCard } from "@/components/recruitment/CountdownShareCard";
import { CREATORS, MODELS, ARCHITECT } from "@/data/characters";

const DEPARTMENTS = [
  { title: "Photography", img: CREATORS[0].img, pos: "50% 10%", desc: "Editorial, portrait & documentary image-making for every Issue.", roles: "Photographer · Photo Assistant · Retoucher", tools: "Digital, 35mm, studio flash, Lightroom", span: "md:col-span-2 md:row-span-2", h: "h-72 md:h-full" },
  { title: "Film & Video", img: CREATORS[1].img, pos: "50% 14%", desc: "Campaign films, manifesto cuts and behind-the-scenes documentary.", roles: "Director · Cinematographer · Editor", tools: "Cinema cameras, Premiere, DaVinci", span: "", h: "h-64" },
  { title: "Beauty Production", img: CREATORS[2].img, pos: "50% 18%", desc: "Graphic, editorial beauty looks designed to read on camera.", roles: "MUA · Hair · Beauty Assistant", tools: "Pro kits, SFX, color theory", span: "", h: "h-64" },
  { title: "Fashion & Styling", img: CREATORS[6].img, pos: "50% 10%", desc: "Wardrobe, styling and the look-is-the-law energy of each campaign.", roles: "Stylist · Wardrobe Assistant · Tailor", tools: "Sourcing, fittings, lookbooks", span: "", h: "h-64" },
  { title: "Writing & Journalism", img: CREATORS[5].img, pos: "52% 30%", desc: "The words that hold every Issue together — features, copy, interviews.", roles: "Writer · Copywriter · Interviewer", tools: "Long-form, scripts, editorial voice", span: "", h: "h-64" },
  { title: "Marketing & Social Media", img: CREATORS[4].img, pos: "50% 12%", desc: "Turning campaigns into moments and moments into movements.", roles: "Strategist · Content Creator · Analyst", tools: "Social-first rollouts, creator cuts", span: "md:col-span-2", h: "h-64" },
  { title: "Creative Direction", img: CREATORS[3].img, pos: "50% 12%", desc: "The vision: concepts, moodboards, identity systems and final calls.", roles: "Creative Director · Art Director · Designer", tools: "Decks, systems, typography", span: "", h: "h-64" },
  { title: "Models / Talent", img: MODELS[1].img, pos: "50% 8%", desc: "The faces of the universe — editorial talent in front of the lens.", roles: "Model · Movement · Featured Talent", tools: "Posing, presence, professionalism", span: "", h: "h-64" },
];

const BENEFITS = [
  ["01", "Portfolio Development", "Leave with real campaign work, not classroom exercises."],
  ["02", "Creative Credits", "Role-specific credits on every Issue you help build."],
  ["03", "Production Experience", "Real call sheets, real deadlines, real sets."],
  ["04", "Industry Mentorship", "Working professionals reviewing your work."],
  ["05", "Collaborative Projects", "One brief. Many creatives. One campaign."],
  ["06", "Leadership Opportunities", "Step up and run a department when you're ready."],
  ["07", "Professional Networking", "Build alongside the people you'll work with for years."],
  ["08", "School / Internship Hours", "Where applicable — always approved by your school."],
];

const CropMarks = () => (
  <span className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true">
    <i className="absolute left-1.5 top-1.5 h-3 w-3 border-l border-t border-gold/70" />
    <i className="absolute right-1.5 top-1.5 h-3 w-3 border-r border-t border-gold/70" />
    <i className="absolute bottom-1.5 left-1.5 h-3 w-3 border-b border-l border-gold/70" />
    <i className="absolute bottom-1.5 right-1.5 h-3 w-3 border-b border-r border-gold/70" />
  </span>
);

// Occasional angled panel edges keep the contact sheet from feeling uniform.
const PANEL_CLIP = {
  1: "polygon(0 0, 100% 2.5%, 100% 100%, 0 97.5%)",
  6: "polygon(0 2.5%, 100% 0, 100% 97.5%, 0 100%)",
};

const DeptPanel = ({ d, i }) => (
  <Reveal delay={(i % 4) * 0.06} className={d.span}>
    <div
      className={`group relative overflow-hidden gold-frame bg-charcoal transition-shadow duration-500 hover:shadow-[0_0_35px_rgba(201,162,39,0.22)] ${d.h} ${i % 3 === 1 ? "md:rotate-[0.4deg]" : i % 3 === 2 ? "md:-rotate-[0.4deg]" : ""}`}
      style={PANEL_CLIP[i] ? { clipPath: PANEL_CLIP[i] } : undefined}
      data-testid={`dept-${d.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="panel-mono absolute inset-0">
        <img src={d.img} alt={d.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ objectPosition: d.pos }} />
      </div>
      <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04] transition-opacity duration-500 group-hover:opacity-[0.09]" />
      <div className="absolute inset-0 bg-gradient-to-t from-onyx via-onyx/25 to-transparent" />
      <CropMarks />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <h3 className="font-display text-2xl uppercase tracking-tight text-cream transition-transform duration-500 group-hover:-translate-y-0.5">{d.title}</h3>
        <div className="max-h-0 overflow-hidden opacity-0 transition-all duration-500 group-hover:max-h-40 group-hover:opacity-100">
          <p className="mt-2 font-body text-sm leading-relaxed text-cream/85">{d.desc}</p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-goldlight">{d.roles}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-silver/60">{d.tools}</p>
        </div>
      </div>
      <span className="absolute left-3 top-3 font-mono text-[8px] uppercase tracking-[0.3em] text-goldlight/60">BLV / {String(i + 1).padStart(3, "0")}</span>
      <span className="absolute right-3 top-3 border border-gold/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Dept {String(i + 1).padStart(2, "0")}</span>
    </div>
  </Reveal>
);

const GoldKicker = ({ children }) => (
  <p className="font-cinzel text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs">{children}</p>
);

export default function Home() {
  const [introDone, setIntroDone] = useState(() => {
    try { return sessionStorage.getItem("blv_intro_session") === "1"; } catch { return false; }
  });

  return (
    <div className="bg-onyx text-cream">
      <AnimatePresence>{!introDone && <CinematicIntro onDone={() => setIntroDone(true)} />}</AnimatePresence>

      {/* HERO — mirrors the intro's final frame so skipping lands on the same story */}
      <section className="grain-dark relative overflow-hidden border-b border-gold/25 bg-onyx" data-testid="home-hero">
        <div className="pointer-events-none absolute left-1/2 top-1/3 h-[80vmin] w-[80vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.12) 0%, transparent 60%)" }} />
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
        <div className="relative mx-auto max-w-[1400px] px-4 pb-20 pt-16 text-center sm:px-6 sm:pb-28 sm:pt-24">
          <GoldKicker>A creative ecosystem</GoldKicker>
          <h1 className="mt-5 font-display uppercase leading-[0.85] tracking-tight text-metallic text-[16vw] sm:text-[8.5rem]">
            <MaskLines lines={["Big", "Little", "Village"]} />
          </h1>
          <p className="mx-auto mt-6 max-w-xl font-body text-base leading-relaxed text-silver sm:text-lg">
            Where emerging talent builds real work, real credits, and real careers — together.
          </p>
          <div className="gold-rule mx-auto mt-8 w-56" />
          <p className="mt-4 font-cinzel text-[10px] uppercase tracking-[0.3em] text-cream/80 sm:text-xs" data-testid="hero-cohort-line">
            Founding Cohort 2027 · Applications open September 5, 2026
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/apply" data-testid="hero-enter-btn" className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform duration-200 hover:-translate-y-0.5">
              Enter the Village
            </Link>
            <Link to="/cohort" data-testid="hero-cohort-btn" className="border border-silver/40 px-8 py-3.5 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors duration-200 hover:border-gold hover:text-goldlight">
              Explore the Cohort
            </Link>
          </div>

          {/* ensemble strip */}
          <div className="mt-16 grid grid-cols-4 gap-2 sm:grid-cols-8 sm:gap-3" data-testid="hero-ensemble">
            {[...CREATORS, MODELS[0]].map((c, i) => (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.6 }} className={`group panel-mono relative overflow-hidden gold-frame ${i % 2 ? "h-28 sm:h-40" : "h-24 sm:h-32"} ${i % 2 ? "sm:mt-6" : ""}`}>
                <img src={c.img} alt={c.name} className="h-full w-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx/70 to-transparent" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 1 — WELCOME */}
      <section className="grain-dark relative border-b border-gold/20 bg-charcoal py-20 sm:py-28" data-testid="home-welcome">
        <div className="relative mx-auto grid max-w-[1300px] items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <GoldKicker>Chapter one</GoldKicker>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl">
              Welcome to<br />Big Little Village
            </h2>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-silver sm:text-lg">
              Big Little Village is a creative development ecosystem where emerging talent gains experience,
              portfolio work, mentorship, credits, collaboration, and opportunities to build alongside other creatives.
            </p>
            <p className="mt-6 font-marker text-lg text-goldlight">different voices. one universe. ✦</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative mx-auto h-[420px] w-full max-w-md sm:h-[480px]">
              <div className="group panel-mono absolute left-0 top-0 h-56 w-2/3 overflow-hidden gold-frame -rotate-2 sm:h-64">
                <img src={MODELS[4].img} alt="" className="h-full w-full object-cover object-top" />
              </div>
              <div className="group panel-mono absolute right-0 top-24 h-64 w-1/2 overflow-hidden gold-frame rotate-2 sm:h-72">
                <img src={CREATORS[0].img} alt="" className="h-full w-full object-cover object-top" />
              </div>
              <div className="group panel-mono absolute bottom-0 left-8 h-44 w-3/5 overflow-hidden gold-frame rotate-1 sm:h-52">
                <img src={CREATORS[1].img} alt="" className="h-full w-full object-cover" style={{ objectPosition: "50% 20%" }} />
              </div>
              <span className="absolute -bottom-2 right-2 font-marker text-sm text-silver/70">the ensemble, assembling…</span>
              <span className="absolute -top-5 left-0 font-mono text-[9px] uppercase tracking-[0.35em] text-silver/40">fig. 01 — the village</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — CREATIVE ECOSYSTEM */}
      <section className="grain-dark relative border-b border-gold/20 bg-onyx py-20 sm:py-28" data-testid="home-ecosystem">
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
          <Reveal>
            <GoldKicker>The creative ecosystem</GoldKicker>
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl">
                Eight departments.<br />One universe.
              </h2>
              <p className="max-w-sm font-body text-sm leading-relaxed text-silver/80">
                Every campaign is built by a multidisciplinary crew. Hover a panel to see what each department does.
              </p>
            </div>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.35em] text-silver/40">BLV / Contact Sheet 000 · Founding Universe</p>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-4">
            {DEPARTMENTS.map((d, i) => (
              <DeptPanel key={d.title} d={d} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — BUILD REAL WORK */}
      <section className="grain-dark relative border-b border-gold/20 bg-charcoal py-20 sm:py-28" data-testid="home-benefits">
        <div className="relative mx-auto max-w-[1200px] px-4 sm:px-6">
          <Reveal>
            <GoldKicker>Why join</GoldKicker>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-7xl">Build real work.</h2>
          </Reveal>
          <div className="mt-12 border-t border-gold/20">
            {BENEFITS.map(([n, title, body], i) => (
              <Reveal key={n} delay={i * 0.04}>
                <div className="group grid items-baseline gap-1 border-b border-gold/20 py-5 transition-colors duration-300 hover:bg-onyx/60 sm:grid-cols-[80px_1fr_1.2fr] sm:gap-6 sm:px-4">
                  <span className="font-cinzel text-sm text-gold">{n}</span>
                  <h3 className="font-display text-2xl uppercase tracking-tight text-cream transition-colors group-hover:text-goldlight sm:text-3xl">{title}</h3>
                  <p className="font-body text-sm leading-relaxed text-silver/75">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — FOUNDING COHORT */}
      <section className="grain-dark relative overflow-hidden bg-onyx py-24 sm:py-32" data-testid="home-cohort">
        <div className="drift-slow pointer-events-none absolute left-1/2 top-1/2 h-[90vmin] w-[90vmin] rounded-full" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.1) 0%, transparent 60%)" }} />
        <div className="vignette pointer-events-none absolute inset-0" />
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.05]" />
        <div className="relative mx-auto max-w-[1100px] px-4 sm:px-6">
          <div className="gold-frame border-breathe relative bg-charcoal/80 p-8 text-center backdrop-blur-sm sm:p-14">
            <div className="gold-rule-anim pointer-events-none absolute inset-x-8 top-0" />
            <div className="pointer-events-none absolute inset-2 border border-gold/20" />
            <Reveal>
              <p className="font-cinzel text-[10px] uppercase tracking-[0.5em] text-gold sm:text-xs">Welcome to Big Little Village</p>
              <div className="relative mx-auto mt-8 h-40 w-40 overflow-hidden rounded-full border border-gold/40 sm:h-48 sm:w-48">
                <img src={ARCHITECT.img} alt="" className="h-full w-full object-cover object-top" style={{ filter: "grayscale(0.7) brightness(0.8)" }} />
                <div className="absolute inset-0 rounded-full shadow-[inset_0_0_40px_rgba(0,0,0,0.8),0_0_50px_rgba(201,162,39,0.25)]" />
              </div>
              <h2 className="mt-10 font-display text-4xl uppercase leading-[0.9] tracking-tight text-metallic sm:text-6xl">
                The first chapter begins.
              </h2>
              <p className="mt-4 font-cinzel text-xs uppercase tracking-[0.35em] text-cream/85 sm:text-sm">
                Big Little Village Founding Cohort · 2027
              </p>
              <div className="gold-rule mx-auto mt-6 w-64" />
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/70">Applications open</p>
              <p className="mt-1 font-display text-3xl uppercase tracking-wide text-gold-metallic sm:text-5xl" data-testid="cohort-open-date">September 5, 2026</p>
              {windowState("student").status === "sealed" && (
                <div className="mt-8">
                  <MissionCountdown kind="student" label="T-minus · Recruitment window opens in" />
                  <div className="mt-6">
                    <CountdownShareCard kind="student" preview={false} />
                  </div>
                </div>
              )}
              <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                {windowState("student").status === "open" ? (
                  <Link to="/apply/student" data-testid="cohort-apply-btn" className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-10 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_40px_rgba(201,162,39,0.35)] transition-transform duration-200 hover:-translate-y-0.5">
                    Submit Your Dossier
                  </Link>
                ) : (
                  <Link to="/cohort" data-testid="cohort-apply-btn" className="btn-sweep border border-gold bg-gradient-to-b from-goldlight to-gold px-10 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-onyx shadow-[0_0_40px_rgba(201,162,39,0.35)] transition-transform duration-200 hover:-translate-y-0.5">
                    Review the Mission Brief
                  </Link>
                )}
                <Link to="/cohort" data-testid="cohort-explore-btn" className="inline-flex items-center gap-2 border border-silver/40 px-8 py-4 font-head text-sm font-extrabold uppercase tracking-[0.2em] text-silver transition-colors hover:border-gold hover:text-goldlight">
                  Explore the Cohort <ArrowRight size={15} />
                </Link>
              </div>
              <p className="mt-8 font-marker text-sm text-silver/60">this isn't just an invite — it's an entry point into a movement.</p>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
